import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import usePortalType from '@rsos/utils/usePortalType';
import { GEOCOMM, _911INFORM } from '../../constants/spatialLayers';
import {
  CALL_OUT_TO_TAB,
  FLOOR_SELECTOR,
} from '../AdditionalData/EllingtonCard/CustomComponents/constants';

const usePatchedDisplaySchema = () => {
  const mapCardData = useSelector(state => state.mapCard.mapCardData);

  // Note: this is brittle, as the `attributes` can be provisioned differently
  // between layers. Agreement between product and devs is that static layers
  // have a `display_schema` property rather than `attributes` being the schema
  // like for dynamic layers
  const getDisplaySchema = useCallback(() => {
    return mapCardData?.layerData?.attributes?.display_schema;
  }, [mapCardData]);

  const isPremiumUser = usePortalType().isPremiumUser;

  const hasEsriMapAccess =
    useSelector(state =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && isPremiumUser;

  const data = mapCardData?.spatialObject?.attributes;

  return patchDisplaySchema({
    layerSource: mapCardData?.layerData?.source,
    data,
    schema: getDisplaySchema(),
    conditions: {
      hasEsriMapAccess,
    },
  });
};

/**
 * Patch the display schema for the UI to render relevant data
 * @param {String} layerSource - source of spatial layer data
 * @param {Object} data - spatial object data
 * @param {Object} schema - display schema
 * @param {Object} conditions - key/value pairs to tell this function how to patch the display schema
 */
export const patchDisplaySchema = ({
  layerSource,
  data,
  schema = {},
  conditions,
}) => {
  let newSchema = schema;

  const handlers = {
    [_911INFORM]: _911informHandler,
    [GEOCOMM]: geocommHandler,
  };
  const handler =
    handlers[
      Object.keys(handlers).find(key => {
        return layerSource?.includes(key);
      })
    ];
  if (handler) {
    newSchema = handler({ data, schema, conditions });
  }
  return newSchema;
};

/**
 * Remove `floor-selector` from the propertiesOrder if it's not esri maps or
 * if it's not in the data
 * @param {Object} data - spatial object data
 * @param {Object} schema - display schema
 * @param {Object} conditions - key/value pairs to tell this function how to
 * patch the display schema
 */
const geocommHandler = ({ data, schema, conditions }) => {
  if (!conditions.hasEsriMapAccess || !data[FLOOR_SELECTOR]) {
    const newSchema = {
      ...schema,
      propertiesOrder: schema.propertiesOrder
        ? schema.propertiesOrder.filter(p => {
            p === FLOOR_SELECTOR;
          })
        : [],
    };
    return newSchema;
  }

  return schema;
};

/**
 * Add `call-out-to-tab` to `propertiesOrder` if it's missing and remove
 * `address` from `propertiesOrder` for feature parity with the existing map
 * card.
 * @param {Object} data - spatial object data
 * @param {Object} schema - display schema
 * @param {Object} conditions - key/value pairs to tell this function how to
 * patch the display schema
 */
const _911informHandler = ({ data, schema, conditions }) => {
  if (!schema.propertiesOrder.find(p => p === CALL_OUT_TO_TAB)) {
    const newSchema = {
      ...schema,
      propertiesOrder: [...schema.propertiesOrder, CALL_OUT_TO_TAB].filter(
        p => p !== 'address',
      ),
    };
    return newSchema;
  }

  return {
    ...schema,
    propertiesOrder: [...schema.propertiesOrder].filter(p => p !== 'address'),
  };
};

export default usePatchedDisplaySchema;
